import { Fragment, type ReactNode } from "react";
import { Section } from "../components/Section";
import { CARS, type Car } from "../lib/cars";

// Every car AJ has owned, oldest first, on a grid-paper board (the shared
// `.board` pattern in index.css — the Letterboxd tapes use it too). The hover
// card opens to the right in columns 1–2 and to the left in 3–4.
export function Cars() {
  return (
    <Section id="cars" label="cars i've owned" title="cars i've owned">
      <p className="mt-5 text-text-dim">{emphasis(INTRO)}</p>
      <div className="board panel mt-8">
        <ol className="board-grid board-grid--cars">
          {CARS.map((car, i) => (
            <CarCell key={car.id} car={car} n={i + 1} popLeft={i % 4 >= 2} />
          ))}
        </ol>
      </div>
    </Section>
  );
}

function CarCell({ car, n, popLeft }: { car: Car; n: number; popLeft: boolean }) {
  const label = `${car.year} ${car.name}`;
  return (
    <li className={`board-cell car-cell${popLeft ? " pop-left" : ""}`} tabIndex={0} aria-label={`${label}, ${car.color}, ${car.trans}`}>
      {car.img ? (
        <img className="car-img" src={car.img} alt="" loading="lazy" />
      ) : (
        <span className="car-img car-silhouette" aria-hidden="true" />
      )}

      <div className="board-pop panel" role="tooltip">
        <div className="etch-label !text-[11px] !text-amber">
          #{String(n).padStart(2, "0")} of {CARS.length}
        </div>
        <div className="mt-1 font-display text-[15px] font-semibold uppercase tracking-wide">{label}</div>
        <div className="mt-1 text-[13px] text-text-dim">{car.trans}</div>
        <p className="mt-2 text-[13px] leading-snug text-text">
          {car.blurb ? emphasis(car.blurb) : <span className="italic text-text-mute">blurb coming soon</span>}
        </p>
      </div>
    </li>
  );
}

// AJ's words, verbatim — `*cash*` renders as italics.
const INTRO =
  "I've loved cars my whole life. I like all cars. Good cars, bad cars, Top Gear stuff, shitboxes, and everything in between. I especially love *cash* cars; I've always loved buying and selling things on Craigslist and Facebook Marketplace. I've helped 20+ friends and family buy and sell their cars privately, but this is me so far. I love 90s Toyotas the most (yes, I'm basic, I'll own a Mk4 Supra one day), but I think next I need a Mk2 MR2. In this same period of time, my wife has had two Mini Coopers and a Beetle. Hover over the cars to read a little blurb about my ownership experience.";

// AJ's blurbs are stored verbatim; `*x*` in them is emphasis, shown as italics.
export function emphasis(text: string): ReactNode {
  return text.split(/(\*[^*]+\*)/g).map((part, i) =>
    part.startsWith("*") && part.endsWith("*") && part.length > 2 ? (
      <em key={i}>{part.slice(1, -1)}</em>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}
